import { useIntl } from "react-intl";
import { newCondition, type ConditionByTypename, type MatchCondition } from "../design-codec";
import { SKILL_LEVELS, toStoragePercent } from "../../../lib/skill-levels";
import { PlusButton } from "@/components/design/PlusButton";
import {
  AiModeChip,
  CompositionEditor,
  OperatorSelect,
  RequirementControls,
  RowShell,
  SELECT_CLASS,
  SubsectionHeader,
  TextValueInput,
} from "./row-primitives";

type ExpertiseCondition = ConditionByTypename<"ExpertiseCondition">;
type ExperienceCondition = ConditionByTypename<"ExperienceCondition">;
type Row = ExpertiseCondition | ExperienceCondition;

const EXPERTISE_OPERATORS: ExpertiseCondition["operator"][] = ["GE", "LE", "EQ"];
const EXPERIENCE_OPERATORS: ExperienceCondition["operator"][] = ["EQ", "NE"];
const DEFAULT_LEVEL_PERCENT = toStoragePercent(3);

export interface FreitextSectionProps {
  conditions: Row[];
  onChange: (next: MatchCondition[]) => void;
  readOnly?: boolean;
  aiOn: boolean;
  onAiChange: (next: boolean) => void;
  // Muss/Kann-Schloss je Zeile anzeigen (spark #151, ADR-0030: Attribut in allen Modi).
  showRequirement?: boolean;
}

export function FreitextSection({
  conditions,
  onChange,
  readOnly,
  aiOn,
  onAiChange,
  showRequirement,
}: FreitextSectionProps) {
  const intl = useIntl();
  function patch(index: number, next: Row) {
    const updated = conditions.slice();
    updated[index] = next;
    onChange(updated as MatchCondition[]);
  }
  function remove(index: number) {
    const updated = conditions.slice();
    updated.splice(index, 1);
    onChange(updated as MatchCondition[]);
  }
  function addSkill() {
    onChange([
      ...(conditions as MatchCondition[]),
      newCondition("ExpertiseCondition", {
        expertise: "",
        level: DEFAULT_LEVEL_PERCENT,
        aiMode: "NO_AI",
        operator: "GE",
      }),
    ]);
  }
  function addJobtitle() {
    onChange([
      ...(conditions as MatchCondition[]),
      newCondition("ExperienceCondition", {
        value: "",
        aiMode: "NO_AI",
        operator: "EQ",
      }),
    ]);
  }

  return (
    <div>
      <SubsectionHeader
        title={intl.formatMessage({ id: "match.freitext.title", defaultMessage: "Expertise & Work Experience" })}
        right={<AiModeChip on={aiOn} onChange={onAiChange} disabled={readOnly} />}
      />
      {conditions.map((c, i) => {
        if (c.__typename === "ExpertiseCondition") {
          return (
            <RowShell key={i} onRemove={() => remove(i)} readOnly={readOnly}>
              {showRequirement ? (
                <RequirementControls
                  requirement={c.requirement}
                  readOnly={readOnly}
                  onChange={(req) => patch(i, { ...c, requirement: req })}
                />
              ) : null}
              <TextValueInput
                value={c.expertise}
                readOnly={readOnly}
                placeholder={intl.formatMessage({ id: "match.freitext.skillPlaceholder", defaultMessage: "e.g. C++, Production Workflows" })}
                onChange={(next) => patch(i, { ...c, expertise: next })}
              />
              <OperatorSelect
                value={c.operator}
                allowed={EXPERTISE_OPERATORS}
                readOnly={readOnly}
                onChange={(op) => patch(i, { ...c, operator: op })}
              />
              <select
                className={SELECT_CLASS}
                value={c.level}
                disabled={readOnly}
                onChange={(e) => patch(i, { ...c, level: Number(e.target.value) })}
                aria-label={intl.formatMessage({ id: "match.freitext.levelLabel", defaultMessage: "Level" })}
              >
                {SKILL_LEVELS.map((l) => (
                  <option key={l.value} value={toStoragePercent(l.value)}>
                    {l.value} - {intl.formatMessage({ id: l.nameId, defaultMessage: l.name })}
                  </option>
                ))}
              </select>
              <CompositionEditor
                value={c.composition}
                readOnly={readOnly}
                onChange={(comp) => patch(i, { ...c, composition: comp })}
              />
            </RowShell>
          );
        }
        return (
          <RowShell key={i} onRemove={() => remove(i)} readOnly={readOnly}>
            {showRequirement ? (
              <RequirementControls
                requirement={c.requirement}
                readOnly={readOnly}
                onChange={(req) => patch(i, { ...c, requirement: req })}
              />
            ) : null}
            <TextValueInput
              value={c.value}
              readOnly={readOnly}
              placeholder={intl.formatMessage({ id: "match.freitext.jobTitlePlaceholder", defaultMessage: "e.g. Senior Designer, Lead Engineer" })}
              onChange={(next) => patch(i, { ...c, value: next })}
            />
            <OperatorSelect
              value={c.operator}
              allowed={EXPERIENCE_OPERATORS}
              readOnly={readOnly}
              onChange={(op) => patch(i, { ...c, operator: op })}
            />
            <CompositionEditor
              value={c.composition}
              readOnly={readOnly}
              onChange={(comp) => patch(i, { ...c, composition: comp })}
            />
          </RowShell>
        );
      })}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-2">
        {!readOnly && <PlusButton label={intl.formatMessage({ id: "match.freitext.addSkill", defaultMessage: "Skill" })} onClick={addSkill} />}
        {!readOnly && <PlusButton label={intl.formatMessage({ id: "match.freitext.addJobTitle", defaultMessage: "Job title" })} onClick={addJobtitle} />}
      </div>
    </div>
  );
}
